'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/components/ui/button';
import { GameDifficulty } from '@/contexts/games/types';
import { cn } from '@/shared/utils/cn';

interface DifficultySelectorProps {
  difficulty: GameDifficulty;
  onDifficultyChange: (difficulty: GameDifficulty) => void;
  className?: string;
}

const DIFFICULTIES: GameDifficulty[] = ['easy', 'medium', 'hard', 'expert'];

/**
 * Универсальный селектор сложности с кнопками-переключателями
 * Используется во всех играх платформы для выбора уровня сложности
 */
export function DifficultySelector({
  difficulty,
  onDifficultyChange,
  className
}: DifficultySelectorProps) {
  const t = useTranslations();

  return (
    <div className={cn('space-y-3', className)} data-testid="difficulty-selector">
      <h3 className="text-sm font-medium text-foreground text-center" data-testid="difficulty-selector-title">
        {t('games.difficulty.title')}
      </h3>

      <div className="flex flex-wrap gap-2 justify-center" data-testid="difficulty-selector-options">
        {DIFFICULTIES.map((level) => (
          <Button
            key={level}
            type="button"
            variant={difficulty === level ? 'default' : 'outline'}
            size="sm"
            onClick={() => onDifficultyChange(level)}
            className={cn(
              'text-xs px-3 py-1 transition-all',
              difficulty === level
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                : 'hover:bg-primary/10'
            )}
            data-testid={`difficulty-option-${level}`}
            data-selected={difficulty === level}
          >
            {t(`games.difficulty.${level}.name`)}
          </Button>
        ))}
      </div>

      {/* Описание выбранной сложности */}
      <p className="text-xs text-muted-foreground text-center max-w-xs mx-auto" data-testid="difficulty-selector-description">
        {t(`games.difficulty.${difficulty}.description`)}
      </p>
    </div>
  );
}
